import { Text, Flex, RadioGroup, Radio, Banner, Box } from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import { ExternalLink } from '@common';

import { SurveyOptions } from '@/components/ABMManagement/types';
import { getLocalizedAppleUrl } from '@/helpers/getLocalizedAppleUrl';

type ABMSurveyProps = {
  optionChecked: string;
  onSelectionChange: (optionChecked: keyof typeof SurveyOptions) => void;
};

const ABMInfoText = () => {
  const { t } = useTranslation('getting-started');
  return (
    <Text>
      {t('abmSurvey.infoBanner.message')}
      <Box mt="1rem">
        <ExternalLink
          href={getLocalizedAppleUrl(
            'https://support.apple.com/guide/apple-business-manager/sign-up-axm402206497/web'
          )}>
          {t('abmSurvey.infoBanner.learnMore')}
        </ExternalLink>
      </Box>
    </Text>
  );
};

const ABMInfoBanner = () => {
  const { t } = useTranslation('getting-started');
  return (
    <Banner
      id="abm-info-banner"
      isFlat
      title={t('abmSurvey.infoBanner.title')}
      message={() => <ABMInfoText />}
    />
  );
};

const ABMWarningText = () => {
  const { t } = useTranslation('getting-started');
  return <Text>{t('abmSurvey.warningBanner.message')}</Text>;
};

const ABMWarningBanner = () => {
  const { t } = useTranslation('getting-started');
  return (
    <Banner
      id="abm-warning-banner"
      isFlat
      intent="warning"
      title={t('abmSurvey.warningBanner.title')}
      message={() => <ABMWarningText />}
    />
  );
};

const ABMSurvey = ({ optionChecked, onSelectionChange }: ABMSurveyProps) => {
  const { t } = useTranslation('getting-started');
  return (
    <Flex vertical p="3rem" rowGap="3rem">
      <Text variant="label-large">{t('abmSurvey.question')}</Text>
      <RadioGroup aria-label="apple-business-manager-survey" stack>
        <Radio
          value={SurveyOptions.YES}
          checked={optionChecked === SurveyOptions.YES}
          onChange={() => onSelectionChange(SurveyOptions.YES)}
          id="abm-is-used-to-manage-devices">
          {t('abmSurvey.options.yes')}
        </Radio>
        <Radio
          value={SurveyOptions.NO}
          checked={optionChecked === SurveyOptions.NO}
          onChange={() => onSelectionChange(SurveyOptions.NO)}
          id="abm-is-not-used-for-device-management">
          {t('abmSurvey.options.no')}
        </Radio>
        <Radio
          value={SurveyOptions.SKIP}
          checked={optionChecked === SurveyOptions.SKIP}
          onChange={() => onSelectionChange(SurveyOptions.SKIP)}
          id="not-want-to-connect-abm-at-this-time">
          {t('abmSurvey.options.skip')}
        </Radio>
      </RadioGroup>
      {optionChecked === SurveyOptions.NO && <ABMInfoBanner />}
      {optionChecked === SurveyOptions.SKIP && <ABMWarningBanner />}
    </Flex>
  );
};

export default ABMSurvey;
